import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, CheckCircle, XCircle, Plane, Building2, Compass, Car } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface AmadeusSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AmadeusSettingsModal({ open, onOpenChange }: AmadeusSettingsModalProps) {
  const { t } = useTranslation('settings');
  const { t: tCommon } = useTranslation('common');

  const [flightMarkupPercent, setFlightMarkupPercent] = useState<number>(5);
  const [hotelMarkupPercent, setHotelMarkupPercent] = useState<number>(10);
  const [activityMarkupPercent, setActivityMarkupPercent] = useState<number>(8);
  const [transferMarkupPercent, setTransferMarkupPercent] = useState<number>(10);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [activeTab, setActiveTab] = useState('flights');

  const { data: flightSettings, isLoading: flightLoading, refetch: refetchFlight } = trpc.flight.getSettings.useQuery(undefined, {
    enabled: open,
  });

  const { data: hotelSettings, isLoading: hotelLoading, refetch: refetchHotel } = trpc.hotel.getSettings.useQuery(undefined, {
    enabled: open,
  });

  const { data: activitySettings, isLoading: activityLoading } = trpc.activities.getSettings.useQuery(undefined, {
    enabled: open,
  });

  const { data: transferSettings, isLoading: transferLoading } = trpc.transfers.getSettings.useQuery(undefined, {
    enabled: open,
  });

  useEffect(() => {
    if (flightSettings) {
      setFlightMarkupPercent(flightSettings.markupPercent);
    }
  }, [flightSettings]);

  useEffect(() => {
    if (hotelSettings) {
      setHotelMarkupPercent(hotelSettings.markupPercent);
    }
  }, [hotelSettings]);

  useEffect(() => {
    if (activitySettings) {
      setActivityMarkupPercent(activitySettings.markupPercent);
    }
  }, [activitySettings]);

  useEffect(() => {
    if (transferSettings) {
      setTransferMarkupPercent(transferSettings.markupPercent);
    }
  }, [transferSettings]);

  const utils = trpc.useUtils();

  const updateFlightMarkupMutation = trpc.flight.updateMarkup.useMutation({
    onSuccess: () => {
      utils.flight.getSettings.invalidate();
      showSuccessToast(t('amadeus.messages.flightMarkupUpdated'));
      setIsSaving(false);
    },
    onError: (error) => {
      showErrorToast(error.message || t('amadeus.messages.flightMarkupError'));
      setIsSaving(false);
    },
  });

  const updateHotelMarkupMutation = trpc.hotel.updateMarkup.useMutation({
    onSuccess: () => {
      utils.hotel.getSettings.invalidate();
      showSuccessToast(t('amadeus.messages.hotelMarkupUpdated'));
      setIsSaving(false);
    },
    onError: (error) => {
      showErrorToast(error.message || t('amadeus.messages.hotelMarkupError'));
      setIsSaving(false);
    },
  });

  const updateActivityMarkupMutation = trpc.activities.updateMarkup.useMutation({
    onSuccess: () => {
      utils.activities.getSettings.invalidate();
      showSuccessToast(t('amadeus.messages.activityMarkupUpdated'));
      setIsSaving(false);
    },
    onError: (error) => {
      showErrorToast(error.message || t('amadeus.messages.activityMarkupError'));
      setIsSaving(false);
    },
  });

  const updateTransferMarkupMutation = trpc.transfers.updateMarkup.useMutation({
    onSuccess: () => {
      utils.transfers.getSettings.invalidate();
      showSuccessToast(t('amadeus.messages.transferMarkupUpdated'));
      setIsSaving(false);
    },
    onError: (error) => {
      showErrorToast(error.message || t('amadeus.messages.transferMarkupError'));
      setIsSaving(false);
    },
  });

  const handleSave = () => {
    const validateAndSave = (percent: number, mutation: { mutate: (data: { markupPercent: number }) => void }) => {
      if (percent < 0 || percent > 100) {
        showErrorToast(t('amadeus.markup.validationError'));
        return;
      }
      setIsSaving(true);
      mutation.mutate({ markupPercent: percent });
    };

    switch (activeTab) {
      case 'flights':
        validateAndSave(flightMarkupPercent, updateFlightMarkupMutation);
        break;
      case 'hotels':
        validateAndSave(hotelMarkupPercent, updateHotelMarkupMutation);
        break;
      case 'activities':
        validateAndSave(activityMarkupPercent, updateActivityMarkupMutation);
        break;
      case 'transfers':
        validateAndSave(transferMarkupPercent, updateTransferMarkupMutation);
        break;
    }
  };

  const isLoading = flightLoading || hotelLoading || activityLoading || transferLoading;

  const handleTestConnection = async () => {
    setIsTesting(true);
    if (activeTab === 'flights') {
      await refetchFlight();
    } else {
      await refetchHotel();
    }
    setIsTesting(false);
  };

  const renderConnectionStatus = (configured?: boolean, connected?: boolean, isHotel?: boolean) => (
    <div className="rounded-lg border p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium">{t('amadeus.connection.title')}</h3>
          <p className="text-sm text-muted-foreground">
            {configured
              ? isHotel
                ? t('amadeus.connection.hotelNote')
                : `${t('amadeus.connection.environment')}: ${flightSettings?.environment === 'production' ? t('amadeus.connection.production') : t('amadeus.connection.test')}`
              : t('amadeus.connection.notConfigured')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {connected ? (
            <div className="flex items-center gap-1 text-green-600">
              <CheckCircle className="h-4 w-4" />
              <span className="text-sm font-medium">{t('amadeus.connection.connected')}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-red-600">
              <XCircle className="h-4 w-4" />
              <span className="text-sm font-medium">
                {configured ? t('amadeus.connection.connectionFailed') : t('amadeus.connection.notConfigured')}
              </span>
            </div>
          )}
        </div>
      </div>
      {!isHotel && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleTestConnection}
          disabled={isTesting || !configured}
        >
          {isTesting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t('amadeus.connection.testConnection')}
        </Button>
      )}
    </div>
  );

  const renderConfigurationWarning = () => (
    <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
      <p className="font-medium mb-1">{t('amadeus.configuration.title')}</p>
      <p>{t('amadeus.configuration.description')}</p>
      <ul className="list-disc list-inside mt-2 space-y-1 text-amber-700">
        <li>{t('amadeus.configuration.clientId')}</li>
        <li>{t('amadeus.configuration.clientSecret')}</li>
        <li>{t('amadeus.configuration.environment')}</li>
      </ul>
    </div>
  );

  const renderMarkupSettings = (
    title: string,
    label: string,
    description: string,
    value: number,
    onChange: (value: number) => void,
    inputId: string
  ) => (
    <div className="space-y-4">
      <h3 className="text-sm font-medium">{title}</h3>
      <div className="space-y-2">
        <Label htmlFor={inputId}>{label}</Label>
        <div className="flex items-center gap-2">
          <Input
            id={inputId}
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={value}
            onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
            className="w-32"
          />
          <span className="text-muted-foreground">%</span>
        </div>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
  );

  const renderExampleCalculation = (basePrice: number, markupPercent: number) => (
    <div className="rounded-lg bg-muted/50 p-4 space-y-2">
      <h4 className="text-sm font-medium">{t('amadeus.example.title')}</h4>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <span className="text-muted-foreground">{t('amadeus.example.basePrice')}:</span>
        <span className="font-mono">${basePrice.toFixed(2)}</span>
        <span className="text-muted-foreground">{t('amadeus.example.markup')} ({markupPercent}%):</span>
        <span className="font-mono text-green-600">+${(basePrice * markupPercent / 100).toFixed(2)}</span>
        <span className="font-medium">{t('amadeus.example.customerPrice')}:</span>
        <span className="font-mono font-semibold">${(basePrice * (1 + markupPercent / 100)).toFixed(2)}</span>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {activeTab === 'flights' && <Plane className="h-5 w-5 text-primary" />}
            {activeTab === 'hotels' && <Building2 className="h-5 w-5 text-primary" />}
            {activeTab === 'activities' && <Compass className="h-5 w-5 text-primary" />}
            {activeTab === 'transfers' && <Car className="h-5 w-5 text-primary" />}
            {t('amadeus.title')}
          </DialogTitle>
          <DialogDescription>
            {t('amadeus.description')}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="flights" className="flex items-center gap-1 text-xs">
              <Plane className="h-3 w-3" />
              {t('amadeus.tabs.flights')}
            </TabsTrigger>
            <TabsTrigger value="hotels" className="flex items-center gap-1 text-xs">
              <Building2 className="h-3 w-3" />
              {t('amadeus.tabs.hotels')}
            </TabsTrigger>
            <TabsTrigger value="activities" className="flex items-center gap-1 text-xs">
              <Compass className="h-3 w-3" />
              {t('amadeus.tabs.activities')}
            </TabsTrigger>
            <TabsTrigger value="transfers" className="flex items-center gap-1 text-xs">
              <Car className="h-3 w-3" />
              {t('amadeus.tabs.transfers')}
            </TabsTrigger>
          </TabsList>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <>
              <TabsContent value="flights" className="space-y-6 mt-4">
                {renderConnectionStatus(flightSettings?.configured, flightSettings?.connected)}
                {!flightSettings?.configured && renderConfigurationWarning()}
                {renderMarkupSettings(
                  t('amadeus.markup.flightTitle'),
                  t('amadeus.markup.flightLabel'),
                  t('amadeus.markup.flightDescription'),
                  flightMarkupPercent,
                  setFlightMarkupPercent,
                  'flightMarkupPercent'
                )}
                {renderExampleCalculation(500, flightMarkupPercent)}
              </TabsContent>

              <TabsContent value="hotels" className="space-y-6 mt-4">
                {renderConnectionStatus(hotelSettings?.configured, hotelSettings?.configured, true)}
                {renderMarkupSettings(
                  t('amadeus.markup.hotelTitle'),
                  t('amadeus.markup.hotelLabel'),
                  t('amadeus.markup.hotelDescription'),
                  hotelMarkupPercent,
                  setHotelMarkupPercent,
                  'hotelMarkupPercent'
                )}
                {renderExampleCalculation(200, hotelMarkupPercent)}
              </TabsContent>

              <TabsContent value="activities" className="space-y-6 mt-4">
                {renderConnectionStatus(flightSettings?.configured, flightSettings?.connected, true)}
                {renderMarkupSettings(
                  t('amadeus.markup.activityTitle'),
                  t('amadeus.markup.activityLabel'),
                  t('amadeus.markup.activityDescription'),
                  activityMarkupPercent,
                  setActivityMarkupPercent,
                  'activityMarkupPercent'
                )}
                {renderExampleCalculation(100, activityMarkupPercent)}
              </TabsContent>

              <TabsContent value="transfers" className="space-y-6 mt-4">
                {renderConnectionStatus(flightSettings?.configured, flightSettings?.connected, true)}
                {renderMarkupSettings(
                  t('amadeus.markup.transferTitle'),
                  t('amadeus.markup.transferLabel'),
                  t('amadeus.markup.transferDescription'),
                  transferMarkupPercent,
                  setTransferMarkupPercent,
                  'transferMarkupPercent'
                )}
                {renderExampleCalculation(150, transferMarkupPercent)}
              </TabsContent>
            </>
          )}
        </Tabs>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            {tCommon('cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isSaving || isLoading}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('saveChanges')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
